import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { backendURL } from '../IPaddress';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const GENRE_SEARCH_FIELDS = ['type', 'description'];
const GENRE_SORT_OPTIONS = [
  { key: 'type', label: 'Type', accessor: (g) => g.type?.toLowerCase() ?? '' },
];




const SchoolGenreComponent = () => {
    const [genres, setGenres] = useState([]);
    const [currentGenre, setCurrentGenre] = useState({ type: '', description: '' });
    const [showForm, setShowForm] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [genreToDelete, setGenreToDelete] = useState(null);
    const { authState } = useAuth();
    const navigate = useNavigate();
    const [showGenreTypeModal, setShowGenreTypeModal] = useState(false);
    const [showGenreDescriptionModal, setShowGenreDescriptionModal] = useState(false);

    const {
        items: visibleGenres,
        resultCount,
        totalCount,
        isFiltered,
        search,
        sort,
        clear,
    } = useListControls(genres, {
        searchFields: GENRE_SEARCH_FIELDS,
        sortOptions: GENRE_SORT_OPTIONS,
    });



    useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
    // Dependency array includes authState to react to its changes
    }, [authState, navigate]);

    // Fetch all genres
    useEffect(() => {
        fetchGenres();
    }, []);


    const fetchGenres = async () => {
        const response = await fetch(`${backendURL}/api/schoolgenres`, {
        method: 'GET',
        credentials: 'include'
        });
        const data = await response.json();
        setGenres(data);
    };

    const handleFormChange = (e) => {
        setCurrentGenre({ ...currentGenre, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if genre type is empty
        if (!currentGenre.type || currentGenre.type.trim() === '') {
            setShowGenreTypeModal(true); // Show the modal if no genre type
            return; // Stop further execution
        }

        // Check if description is empty
        if (!currentGenre.description || currentGenre.description.trim() === '') {
            setShowGenreDescriptionModal(true); // Show the modal if no description
            return; // Stop further execution
        }

        if (currentGenre.genre_id) {
            await updateGenre(currentGenre.genre_id, currentGenre);
        } else {
            await createGenre(currentGenre);
        }
        setCurrentGenre({ type: '', description: '' });
        setShowForm(false);
        fetchGenres();
    };



    const createGenre = async (genreData) => {
        await fetch(`${backendURL}/api/schoolgenres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(genreData),
        credentials: 'include'
        });
    };

    const updateGenre = async (genreId, genreData) => {
        await fetch(`${backendURL}/api/schoolgenres/${genreId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(genreData),
        credentials: 'include'
        });
    };

    const deleteGenre = async () => {
        if (genreToDelete) {
          await fetch(`${backendURL}/api/schoolgenres/${genreToDelete}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          setGenreToDelete(null);
          setShowModal(false);
          fetchGenres();
        }
    };

    const editGenre = (genre) => {
        setCurrentGenre(genre);
        setShowForm(true);
    };

    const handleCancel = () => {
        setCurrentGenre({ type: '', description: '' });
        setShowForm(false);
      }

    const handleToggleForm = () => {
        if (!showForm) {
            setCurrentGenre({ type: '', description: '' });
        }
        setShowForm(!showForm);
    };

    const genreForm = (
        <Form onSubmit={handleSubmit}>
            <FormGrid>
                <Form.Group controlId="type">
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                    type="text"
                    placeholder="Enter genre type"
                    name="type"
                    onChange={handleFormChange}
                    value={currentGenre.type}
                    />
                </Form.Group>
                <Form.Group controlId="description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                    as="textarea"
                    placeholder="Enter genre description"
                    name="description"
                    onChange={handleFormChange}
                    value={currentGenre.description}
                    />
                </Form.Group>
                <FormActions>
                    <Button type="submit">Submit</Button>
                    <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                </FormActions>
            </FormGrid>
        </Form>
    );

    return (
        <div>
        <CrudListShell
            addLabel="Add New Genre"
            listLabel="Show Genres List"
            showForm={showForm}
            onToggleForm={handleToggleForm}
            tableHeading="School Genres"
            form={genreForm}
            filters={(
                <FilterBar
                    summary={(
                        <ResultSummary
                            count={resultCount}
                            total={totalCount}
                            noun="genre"
                            onClear={clear}
                            isFiltered={isFiltered}
                        />
                    )}
                >
                    <SearchInput label="Search genres" placeholder="Type or description" {...search} />
                    <SortControl {...sort} />
                </FilterBar>
            )}
            emptyState={(
                <tr>
                    <td>
                        <ScheduleEmptyState>No genres have been added yet.</ScheduleEmptyState>
                    </td>
                </tr>
            )}
        >
            {visibleGenres.map((genre) => (
                <ExpandableRow
                    key={genre.genre_id}
                    main={{ title: genre.type, subtitle: genre.description || 'No description provided.' }}
                    actions={(
                        <>
                            <Button size="sm" onClick={() => editGenre(genre)} aria-label="Edit genre">
                                <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                                size="sm"
                                variant="danger"
                                onClick={() => {
                                    setGenreToDelete(genre.genre_id);
                                    setShowModal(true);
                                }}
                                aria-label="Delete genre"
                            >
                                <i className="bi bi-trash3"></i>
                            </Button>
                        </>
                    )}
                />
            ))}
        </CrudListShell>

        <ConfirmDeleteModal
            show={showModal}
            onHide={() => setShowModal(false)}
            onConfirm={deleteGenre}
            bodyText="Are you sure you want to delete this genre?"
        />
      <Modal show={showGenreTypeModal} onHide={() => setShowGenreTypeModal(false)}>
        <Modal.Header closeButton>
            <Modal.Title>Missing Genre Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a type for the new school genre.</Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGenreTypeModal(false)}>
            Close
            </Button>
        </Modal.Footer>
        </Modal>
        <Modal show={showGenreDescriptionModal} onHide={() => setShowGenreDescriptionModal(false)}>
        <Modal.Header closeButton>
            <Modal.Title>Missing Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a description for the new school genre.</Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGenreDescriptionModal(false)}>
            Close
            </Button>
        </Modal.Footer>
        </Modal>
        </div>
    );
};

export default SchoolGenreComponent;
